import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import BannerCustom from "components/Banners/BannerCustom";
import ModalV2 from "components/ModalV2/ModalV2";
import ModalV2Buttons from "components/ModalV2Buttons/ModalV2Buttons";
import useAppDispatch, { useReduxDispatch } from "hooks/useAppDispatch";
import sandboxIcon from "img/illustrations/general/sandbox.svg";
import {
  clearSandboxError,
  createSandbox,
  fetchSandboxInfo,
} from "../model/sandboxSlice";
import {
  generateSandboxName,
  validateSandboxName,
} from "../model/sandboxModel";
import "../Sandbox.sass";

const STEP_CONFIGURATION = 1;
const STEP_NAME = 2;

export default function CreateSandboxModal({ open, onClose, onCreated }) {
  const { t } = useTranslation("productSettings");
  const dispatch = useAppDispatch();
  const reduxDispatch = useReduxDispatch();
  const creating = useSelector((state) => Boolean(state.sandbox?.creating));
  const serverError = useSelector((state) => state.sandbox?.error);
  const [step, setStep] = useState(STEP_CONFIGURATION);
  const [copy, setCopy] = useState(null);
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const nameValidationMessages = useMemo(
    () => ({
      required: t("sandbox.create.nameRequired", {
        defaultValue: "Enter a sandbox name.",
      }),
      tooLong: t("sandbox.create.nameTooLong", {
        defaultValue: "Sandbox name must be 80 characters or fewer.",
      }),
    }),
    [t],
  );

  const reset = useCallback(() => {
    setStep(STEP_CONFIGURATION);
    setCopy(null);
    setName(generateSandboxName());
    setNameTouched(false);
    reduxDispatch(clearSandboxError());
  }, [reduxDispatch]);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const nameError = useMemo(
    () =>
      nameTouched
        ? validateSandboxName(name, nameValidationMessages)
        : "",
    [name, nameTouched, nameValidationMessages],
  );

  const close = useCallback(() => {
    if (creating) return;
    reset();
    onClose();
  }, [creating, onClose, reset]);

  const submit = useCallback(async () => {
    setNameTouched(true);
    if (validateSandboxName(name, nameValidationMessages)) return;

    try {
      await dispatch(createSandbox({ copy, name: name.trim() }));
      onCreated?.();
      close();
      dispatch(fetchSandboxInfo()).catch(() => {});
    } catch {
      // The server message is rendered from the sandbox slice.
    }
  }, [
    close,
    copy,
    dispatch,
    name,
    nameValidationMessages,
    onCreated,
  ]);

  const configurationContent = (
    <div className="SandboxCreate__options">
      <button
        type="button"
        className={`SandboxCreate__option ${copy === true ? "is-selected" : ""}`}
        onClick={() => setCopy(true)}
      >
        <div className="SandboxCreate__optionVisual">
          <span className="SandboxCreate__sourceIcon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M5 4h14v15H5zM8 4V2h8v2M8 9h8M8 13h5" />
            </svg>
          </span>
          <span className="SandboxCreate__connector" aria-hidden="true">→</span>
          <span className="SandboxCreate__sourceIcon" aria-hidden="true">
            <img src={sandboxIcon} alt="" />
          </span>
        </div>
        <span className="SandboxCreate__optionCopy">
          <strong>
            {t("sandbox.create.copyAccount", { defaultValue: "Copy account" })}
          </strong>
          <span>
            {t("sandbox.create.copyAccountDescription", {
              defaultValue:
                "Settings and capabilities will be modeled after your live account.",
            })}
          </span>
        </span>
      </button>

      <button
        type="button"
        className={`SandboxCreate__option ${copy === false ? "is-selected" : ""}`}
        onClick={() => setCopy(false)}
      >
        <div className="SandboxCreate__optionVisual">
          <span className="SandboxCreate__plus" aria-hidden="true">+</span>
          <span className="SandboxCreate__sourceIcon" aria-hidden="true">
            <img src={sandboxIcon} alt="" />
          </span>
        </div>
        <span className="SandboxCreate__optionCopy">
          <strong>
            {t("sandbox.create.newSandbox", {
              defaultValue: "Create new sandbox",
            })}
          </strong>
          <span>
            {t("sandbox.create.newSandboxDescription", {
              defaultValue: "Nothing will be copied from your live account.",
            })}
          </span>
        </span>
      </button>
    </div>
  );

  const nameContent = (
    <div className="SandboxCreate__name">
      {serverError ? (
        <BannerCustom status="critical" message={serverError} />
      ) : null}
      <div className={`SandboxCreate__field ${nameError ? "has-error" : ""}`}>
        <input
          autoFocus
          value={name}
          maxLength={80}
          onChange={(event) => {
            setName(event.target.value);
            if (nameTouched) setNameTouched(true);
          }}
          onBlur={() => setNameTouched(true)}
          aria-label={t("sandbox.create.nameLabel", {
            defaultValue: "Sandbox name",
          })}
          aria-invalid={Boolean(nameError)}
        />
        <button
          type="button"
          className="SandboxCreate__regenerate"
          onClick={() => {
            setName(generateSandboxName());
            setNameTouched(false);
          }}
        >
          <span aria-hidden="true">↻</span>
          {t("sandbox.create.regenerate", { defaultValue: "Regenerate" })}
        </button>
      </div>
      {nameError ? <div className="SandboxCreate__error">{nameError}</div> : null}
    </div>
  );

  return (
    <ModalV2
      open={open}
      onClose={close}
      width={step === STEP_CONFIGURATION ? 720 : 640}
      className="SandboxCreateModal"
      closeOnOverlayClick={!creating}
      closeOnEsc={!creating}
      title={
        step === STEP_CONFIGURATION
          ? t("sandbox.create.configurationTitle", {
              defaultValue: "Starting configuration",
            })
          : t("sandbox.create.nameTitle", { defaultValue: "Name sandbox" })
      }
      description={
        step === STEP_CONFIGURATION
          ? t("sandbox.create.configurationDescription", {
              defaultValue: "Choose what to include in your new sandbox.",
            })
          : t("sandbox.create.nameDescription", {
              defaultValue:
                "We generated a name for you, but feel free to change it now or later.",
            })
      }
      content={step === STEP_CONFIGURATION ? configurationContent : nameContent}
      footer={
        step === STEP_CONFIGURATION ? (
          <ModalV2Buttons
            onCancel={close}
            onConfirm={() => setStep(STEP_NAME)}
            cancelText={t("sandbox.cancel", { defaultValue: "Cancel" })}
            confirmText={t("sandbox.create.next", { defaultValue: "Next" })}
            confirmDisabled={copy == null}
          />
        ) : (
          <ModalV2Buttons
            onCancel={() => {
              reduxDispatch(clearSandboxError());
              setStep(STEP_CONFIGURATION);
            }}
            onConfirm={submit}
            cancelText={t("sandbox.create.back", { defaultValue: "Back" })}
            confirmText={t("sandbox.create.submit", {
              defaultValue: "Create sandbox",
            })}
            confirmDisabled={Boolean(
              validateSandboxName(name, nameValidationMessages),
            )}
            confirmLoading={creating}
          />
        )
      }
    />
  );
}
